<script setup lang="ts">
import CookieBanner from './CookieBanner.vue';
import { AuthLink } from './ui';
import logoUrl from '@/images/auth/spireworks-icon.png';

interface Link {
  url: string;
  text: string;
}

interface Props {
  title?: string;
  topLinks?: Link[];
  bottomLinks?: Link[];
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Welcome',
  topLinks: () => [
    { url: '/users/password/new', text: 'Forgot password?' },
    { url: '/users/confirmation/new', text: "Didn't receive confirmation instructions?" },
    { url: '/users/unlock/new', text: "Didn't receive unlock instructions?" },
  ],
  bottomLinks: () => [{ url: '/users/request', text: 'Not a member yet? Request access' }],
});

const quote = '" An open system to share in moments of discovery, play, and next generation civic engagement."';
const subQuote = 'MARK DOMINO, SPIREWORKS CREATOR';
</script>

<template>
  <div class="min-h-dvh w-full bg-sw-dark flex flex-row font-[Graphik,system-ui,sans-serif]">
    <!-- Content Section (Left) -->
    <div class="w-full max-w-none mx-[7%] pt-[17.5px] md:w-[30%] md:max-w-[340px] md:mx-[10%] md:pt-[60px]">
      <!-- Logo -->
      <div class="text-center mb-0">
        <a href="/" class="inline-block transition-opacity duration-200 hover:opacity-70">
          <img :src="logoUrl" alt="Spireworks" class="h-24 w-auto md:h-40" />
        </a>
      </div>

      <!-- Title -->
      <h1
        class="text-white text-center text-[1.875rem] font-bold leading-[1.14] pb-[30.5px] m-0 md:text-[42px] md:pb-10"
      >
        {{ title }}
      </h1>

      <!-- Form Content (slot) -->
      <div class="mb-0">
        <slot />
      </div>

      <!-- Top Links -->
      <ul v-if="topLinks.length > 0" class="list-none m-0 p-0 pt-form-gap">
        <li v-for="link in topLinks" :key="link.url" class="py-2.5">
          <AuthLink :href="link.url">{{ link.text }}</AuthLink>
        </li>
      </ul>

      <!-- Separator -->
      <hr v-if="bottomLinks.length > 0" class="w-2.5 ml-0 border border-sw-purple opacity-50 my-4" />

      <!-- Bottom Links -->
      <ul v-if="bottomLinks.length > 0" class="list-none m-0 p-0">
        <li v-for="link in bottomLinks" :key="link.url" class="py-2.5">
          <AuthLink :href="link.url">{{ link.text }}</AuthLink>
        </li>
      </ul>
    </div>

    <!-- Quote Section (Right) - Hidden on mobile -->
    <div
      class="hidden md:flex w-[70%] items-center justify-center text-center bg-cover bg-no-repeat bg-[position:42%_center] bg-[image:linear-gradient(to_bottom,#111625,rgba(17,22,37,0.7)),url('/images/auth-background.jpg')]"
    >
      <div class="max-w-[600px] px-8">
        <h1 class="text-white text-[2.625rem] font-bold leading-[1.14] mb-[25px]">
          {{ quote }}
        </h1>
        <h4 class="text-white text-sm font-medium leading-[1.2] opacity-80 tracking-[2px] uppercase m-0">
          {{ subQuote }}
        </h4>
      </div>
    </div>

    <!-- Cookie Banner -->
    <CookieBanner />
  </div>
</template>
